// Simple presence manager for WebSocket-based presence
// Uses direct Socket.IO events instead of RTK Query patterns

import type { PresenceState } from './types';

export class PresenceManager {
  private static instance: PresenceManager;
  private presenceState: PresenceState = {};
  private listeners: Set<(state: PresenceState) => void> = new Set();
  private readonly MAX_ENTRIES = 500;

  static getInstance(): PresenceManager {
    if (!PresenceManager.instance) {
      PresenceManager.instance = new PresenceManager();
    }
    return PresenceManager.instance;
  }

  updateUserPresence(userId: string, status: 'online' | 'offline' | 'away', lastSeen?: number) {
    const existing = this.presenceState[userId];
    this.presenceState[userId] = {
      status,
      // Use provided lastSeen, fall back to existing lastSeen, only use Date.now() for online status
      lastSeen: lastSeen || existing?.lastSeen || (status === 'online' ? Date.now() : 0),
      updatedAt: Date.now()
    };

    // Evict oldest entries if over max size
    const keys = Object.keys(this.presenceState);
    if (keys.length > this.MAX_ENTRIES) {
      const sorted = keys.sort((a, b) =>
        this.presenceState[a].updatedAt - this.presenceState[b].updatedAt
      );
      const toRemove = sorted.slice(0, keys.length - this.MAX_ENTRIES);
      for (const key of toRemove) {
        delete this.presenceState[key];
      }
    }

    this.notifyListeners();
  }

  getUserPresence(userId: string) {
    return this.presenceState[userId];
  }

  getAllPresence(): PresenceState {
    return { ...this.presenceState };
  }

  subscribe(listener: (state: PresenceState) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.getAllPresence()));
  }

  // Mark users as offline if not updated in 5 minutes, delete if stale for 30+ minutes
  cleanup() {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;
    const thirtyMinutesAgo = now - 30 * 60 * 1000;
    let changed = false;

    Object.keys(this.presenceState).forEach(userId => {
      const entry = this.presenceState[userId];
      if (entry.updatedAt < thirtyMinutesAgo) {
        // Delete entries older than 30 minutes to prevent unbounded growth
        delete this.presenceState[userId];
        changed = true;
      } else if (entry.updatedAt < fiveMinutesAgo && entry.status !== 'offline') {
        // Mark as offline if not updated in 5 minutes
        this.presenceState[userId].status = 'offline';
        changed = true;
      }
    });

    if (changed) {
      this.notifyListeners();
    }
  }
}

export const presenceManager = PresenceManager.getInstance();